import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    ClickAwayListener,
    Grow,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Paper,
    Popper
} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TimerIcon from "@mui/icons-material/Timer";
import SchoolIcon from "@mui/icons-material/School";

export interface SplitFloatingActionButtonViewProps {
    reviewCount: number
    lessonCount: number
}

export default function SplitFloatingActionButtonView({reviewCount, lessonCount}: SplitFloatingActionButtonViewProps) {

    const [menuOpen, setMenuOpen] = useState(false);
    const [anchorWidth, setAnchorWidth] = useState<number>();
    const anchorRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    useEffect(() => {
        const node = anchorRef.current;
        if (!node) return;

        const updateWidth = () => setAnchorWidth(node.offsetWidth);
        updateWidth();

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(node);
        return () => resizeObserver.disconnect();
    }, []);

    const hasReviews = reviewCount > 0;
    const hasLessons = lessonCount > 0;

    type ActionKind = 'review' | 'lesson';
    const renderActionIcon = (kind: ActionKind, small?: boolean) =>
        kind === 'review'
            ? <TimerIcon fontSize={small ? 'small' : undefined}/>
            : <SchoolIcon fontSize={small ? 'small' : undefined}/>;

    const mainAction = hasReviews
        ? {label: `Start review (${reviewCount})`, link: "/review", kind: 'review' as ActionKind, disabled: false}
        : hasLessons
            ? {label: "Start lesson", link: "/lesson", kind: 'lesson' as ActionKind, disabled: false}
            : {label: "No lessons", link: "/lesson", kind: 'lesson' as ActionKind, disabled: true};

    const dropdownAction = hasReviews
        ? hasLessons
            ? {label: "Start lesson", link: "/lesson", kind: 'lesson' as ActionKind, disabled: false}
            : {label: "No lessons", link: "/lesson", kind: 'lesson' as ActionKind, disabled: true}
        : {label: "No reviews", link: "/review", kind: 'review' as ActionKind, disabled: true};

    const toggleMenu = () => setMenuOpen((prevOpen) => !prevOpen);

    const closeMenu = (event: Event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setMenuOpen(false);
    };

    const glowColor = theme.palette.info.light;
    const buttonSx = {
        bgcolor: 'info.light',
        color: theme.palette.getContrastText(glowColor),
        '&:hover': {bgcolor: 'info.light'},
        '&.Mui-disabled': {
            bgcolor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
        },
    };

    return (
        <Box sx={{position: 'fixed', bottom: 56, right: 56, zIndex: 1200}}>
            <ButtonGroup
                variant="contained"
                ref={anchorRef}
                sx={{
                    borderRadius: 7,
                    animation: 'fabGlowPulse 2.2s ease-in-out infinite',
                    boxShadow: `0 0 10px 2px ${alpha(glowColor, 0.6)}`,
                    '@keyframes fabGlowPulse': {
                        '0%, 100%': {boxShadow: `0 0 10px 2px ${alpha(glowColor, 0.5)}`},
                        '50%': {boxShadow: `0 0 24px 8px ${alpha(glowColor, 0.9)}`},
                    },
                    '& .MuiButtonGroup-firstButton': {
                        borderColor: theme.palette.info.contrastText,
                    },
                }}
            >
                <Button
                    href={mainAction.disabled ? undefined : mainAction.link}
                    disabled={mainAction.disabled}
                    startIcon={renderActionIcon(mainAction.kind)}
                    sx={{...buttonSx, borderRadius: '28px 0 0 28px', px: 3, py: 1.5, fontSize: '1.1rem', textTransform: 'none'}}
                >
                    {mainAction.label}
                </Button>
                <Button
                    size="small"
                    aria-controls={menuOpen ? 'split-action-menu' : undefined}
                    aria-expanded={menuOpen ? 'true' : undefined}
                    aria-label="more actions"
                    aria-haspopup="menu"
                    onClick={toggleMenu}
                    sx={{...buttonSx, borderRadius: '0 28px 28px 0'}}
                >
                    <ArrowDropDownIcon
                        sx={{
                            transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease-in-out',
                        }}
                    />
                </Button>
            </ButtonGroup>
            <Popper
                open={menuOpen}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="top-end"
                transition
                disablePortal
                sx={{zIndex: 1200, width: anchorWidth}}
            >
                {({TransitionProps, placement}) => (
                    <Grow
                        {...TransitionProps}
                        style={{transformOrigin: placement === 'top-end' ? 'right bottom' : 'right top'}}
                    >
                        <Paper sx={{borderRadius: 7, mb: 1, width: '100%', backgroundColor: theme.palette.info.light, color: theme.palette.info.contrastText}}>
                            <ClickAwayListener onClickAway={closeMenu}>
                                <MenuList id="split-action-menu" autoFocusItem={menuOpen}>
                                    <MenuItem
                                        component={dropdownAction.disabled ? 'li' : 'a'}
                                        href={dropdownAction.disabled ? undefined : dropdownAction.link}
                                        disabled={dropdownAction.disabled}
                                        onClick={() => setMenuOpen(false)}
                                        sx={{borderRadius: 7, mx: 0.5, my: 0}}
                                    >
                                        <ListItemIcon sx={dropdownAction.disabled ? undefined : {color: 'inherit'}}>
                                            {renderActionIcon(dropdownAction.kind, true)}
                                        </ListItemIcon>
                                        <ListItemText>{dropdownAction.label}</ListItemText>
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Box>
    );
}
